import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch, type Resolver } from 'react-hook-form';
import { useTender, useCreateTender, useUpdateTender, useBidTypes, useTenderCategories } from '@/lib/api/hooks/tender';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import { showToast } from '@/lib/toast';
import { ChevronLeftIcon } from '@/components/icons';
import { Button, CustomSelect, RichTextEditor } from '@/components/ui';
import FileUpload from '@/components/ui/FileUpload';
import Input from '@/components/ui/Input/Input';
import Calendar from '@/components/ui/Calendar/Calendar';
import { Heading, Label, Paragraph } from '@/components/ui/Typography';
import TrashIcon from '@/assets/icons/TrashIcon';
import { Calendar1, LucideFiles } from 'lucide-react';

interface UploadedFile {
  name: string;
  size: number;
  url: string;
}

interface TenderFormData {
  tenderTitle: string;
  bidType: string;
  tenderCategory: string;
  startDate: string;
  endDate: string;
  description: string;
  submissionEmail: string;
  tenderUrl: string;
}

const DEFAULT_VALUES: TenderFormData = {
  tenderTitle: '',
  bidType: '',
  tenderCategory: '',
  startDate: '',
  endDate: '',
  description: '',
  submissionEmail: '',
  tenderUrl: ''
};

const toDateOnly = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const DraftTenderSchema = z.object({
  tenderTitle: z.string(),
  bidType: z.string(),
  tenderCategory: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  description: z.string(),
  submissionEmail: z
    .string()
    .trim()
    .refine((value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value), {
      message: 'Please enter a valid email address'
    }),
  tenderUrl: z.string()
});

const PublishTenderSchema = DraftTenderSchema.superRefine((data, ctx) => {
  if (!data.tenderTitle.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tenderTitle'], message: 'Tender title is required' });
  }

  if (!data.bidType.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bidType'], message: 'Bid type is required' });
  }

  if (!data.startDate.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['startDate'], message: 'Start date is required' });
  }

  if (!data.endDate.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'], message: 'End date is required' });
  }

  if (data.startDate && data.endDate && data.endDate < data.startDate) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'], message: 'End date cannot be before start date' });
  }

  if (!data.description.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['description'], message: 'Description is required' });
  }

  if (!data.submissionEmail.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['submissionEmail'], message: 'Submission email is required' });
  }

  if (!data.tenderCategory.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['tenderCategory'], message: 'Tender category is required' });
  }
});

const TenderEditor: React.FC = () => {
  const { tenderId } = useParams<{ tenderId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const isEditMode = Boolean(tenderId);
  const existingTender = location.state?.tender;
  const submitIntentRef = useRef<'DRAFT' | 'PUBLISH'>('DRAFT');
  const fileUploadRef = useRef<HTMLDivElement>(null);

  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [serverUploadedFile, setServerUploadedFile] = useState<UploadedFile | null>(null);
  const [uploadKey, setUploadKey] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isLocalUpload, setIsLocalUpload] = useState(false);

  const draftResolver = zodResolver(DraftTenderSchema);
  const publishResolver = zodResolver(PublishTenderSchema);

  const resolver: Resolver<TenderFormData> = (values, context, options) => {
    if (submitIntentRef.current === 'PUBLISH') {
      return publishResolver(values, context, options);
    }

    return draftResolver(values, context, options);
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<TenderFormData>({
    defaultValues: DEFAULT_VALUES,
    resolver,
    mode: 'onChange'
  });

  const watchTitle = useWatch({ control, name: 'tenderTitle' });

  const { data: tenderData, isLoading } = useTender(tenderId);
  const { data: bidTypesData, isLoading: isLoadingBidTypes } = useBidTypes();
  const { data: tenderCategoriesData, isLoading: isLoadingTenderCategories } = useTenderCategories();
  const createMutation = useCreateTender();
  const updateMutation = useUpdateTender();
  const uploadMutation = useFileUpload();

  const isBusy = isSubmitting || isUploading || isDraftSaving || isPublishing;

  const bidTypeOptions = useMemo(
    () =>
      (bidTypesData?.data ?? []).map((type: { value: string; displayName: string }) => ({
        value: type.value,
        label: type.displayName
      })),
    [bidTypesData?.data]
  );

  const tenderCategoryOptions = useMemo(
    () =>
      (tenderCategoriesData?.data ?? []).map((category: { value: string; displayName: string }) => ({
        value: category.value,
        label: category.displayName
      })),
    [tenderCategoriesData?.data]
  );

  useEffect(() => {
    if (!(isEditMode && (tenderData?.data || existingTender))) {
      return;
    }

    const tender = tenderData?.data || existingTender;

    reset({
      tenderTitle: tender.tenderTitle || '',
      bidType: tender.bidType || '',
      tenderCategory: tender.tenderCategory || '',
      startDate: tender.startDate ? String(tender.startDate).slice(0, 10) : '',
      endDate: tender.endDate ? String(tender.endDate).slice(0, 10) : '',
      description: tender.description,
      submissionEmail: tender.submissionEmail || '',
      tenderUrl: tender.tenderUrl || ''
    });

    if (tender.tenderUrl) {
      const urlParts = tender.tenderUrl.split('/');
      const filename = urlParts[urlParts.length - 1] || 'tender.pdf';
      const fileInfo = { name: filename, size: 0, url: tender.tenderUrl };
      setUploadedFile(fileInfo);
      setServerUploadedFile(fileInfo);
      setIsLocalUpload(false);
    } else {
      setUploadedFile(null);
      setServerUploadedFile(null);
      setIsLocalUpload(false);
    }

    setUploadKey((prev) => prev + 1);
  }, [isEditMode, tenderData, existingTender, reset]);

  const uploadFileToServer = async (file: File) => {
    setIsUploading(true);

    try {
      const uploadResult = await uploadMutation.mutateAsync({
        file,
        category: 'DOCUMENT'
      });

      if (!uploadResult.success || !uploadResult.data?.fileUrl) {
        throw new Error('File upload failed');
      }

      const fileInfo: UploadedFile = {
        name: file.name,
        size: file.size,
        url: uploadResult.data.fileUrl
      };

      setUploadedFile(fileInfo);
      setIsLocalUpload(true);
      setValue('tenderUrl', uploadResult.data.fileUrl, { shouldValidate: true, shouldDirty: true });
      setUploadKey((prev) => prev + 1);

      showToast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload failed:', error);
      showToast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelection = async (files: File[] | null) => {
    if (!files || files.length === 0) {
      return;
    }

    await uploadFileToServer(files[0]);
  };

  const handleRemoveLocalUpload = () => {
    if (serverUploadedFile) {
      setUploadedFile(serverUploadedFile);
      setValue('tenderUrl', serverUploadedFile.url, { shouldValidate: true, shouldDirty: true });
      setIsLocalUpload(false);
      setUploadKey((prev) => prev + 1);
      return;
    }

    setUploadedFile(null);
    setValue('tenderUrl', '', { shouldValidate: true, shouldDirty: true });
    setIsLocalUpload(false);
    setUploadKey((prev) => prev + 1);
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(0)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const submitWithStatus = async (data: TenderFormData, status: 'DRAFT' | 'PUBLISH') => {
    const setLoading = status === 'DRAFT' ? setIsDraftSaving : setIsPublishing;
    setLoading(true);

    try {
      const payload = {
        tenderTitle: data.tenderTitle.trim(),
        bidType: data.bidType,
        startDate: data.startDate,
        endDate: data.endDate,
        description: data.description || '',
        submissionEmail: data.submissionEmail.trim(),
        tenderUrl: data.tenderUrl || "",
        status,
        tenderCategory: data.tenderCategory
      };

      if (tenderId) {
        await updateMutation.mutateAsync({
          tenderId,
          data: payload
        });
        showToast.success(status === 'PUBLISH' ? 'Tender published successfully' : 'Tender saved as draft successfully');
      } else {
        await createMutation.mutateAsync(payload);
        showToast.success(status === 'PUBLISH' ? 'Tender published successfully' : 'Tender created as draft successfully');
      }

      navigate('/admin/tenders');
    } catch (error) {
      console.error('Failed to save tender:', error);
      showToast.error(error instanceof Error ? error.message : 'Failed to save tender');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading && tenderId) {
    return (
      <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-10'>
        <div className='text-center'>
          <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-wine-100' />
          <p className='mt-4 text-sm text-gray-500'>Loading tender...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-white rounded-xl relative'>
      <header className='p-4 flex bg-white z-10 sticky top-0 justify-between items-center border-b border-gray-200'>
        <button
          onClick={() => navigate('/admin/tenders')}
          className='flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 disabled:opacity-50'
          disabled={isBusy}
        >
          <ChevronLeftIcon className='w-5 h-5' />
          Go back
        </button>
        <div className='flex items-center gap-4'>
          <Button
            type='button'
            variant='secondary'
            disabled={isBusy}
            loading={isDraftSaving}
            onClick={() => {
              submitIntentRef.current = 'DRAFT';
              void handleSubmit((formData) => submitWithStatus(formData, 'DRAFT'))();
            }}
          >
            Save as Draft
          </Button>
          <Button
            type='button'
            disabled={isBusy}
            loading={isPublishing || isUploading}
            onClick={() => {
              submitIntentRef.current = 'PUBLISH';
              void handleSubmit((formData) => submitWithStatus(formData, 'PUBLISH'))();
            }}
          >
            Publish
          </Button>
        </div>
      </header>

      <div className='max-w-5xl mx-auto'>

    

      <div className='border-b border-gray-200 px-4 py-4 md:px-7'>
        <h1 className='mb-3 text-2xl font-bold text-gray-900'>{isEditMode ? 'Edit Tender' : 'New Tender'}</h1>
        <nav className='flex items-center gap-2 text-sm'>
          <button
            onClick={() => navigate('/admin/tenders')}
            className='text-wine-100 hover:text-wine-200 hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
            disabled={isBusy}
          >
            All Tenders
          </button>
          {isEditMode ? (
            <>
              <span className='text-gray-400'>/</span>
              <button
                onClick={() => navigate(`/admin/tenders/${tenderId}`)}
                className='text-wine-100 hover:text-wine-200 hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
                disabled={isBusy}
              >
                {(watchTitle || '').trim() || 'Tender'}
              </button>
              <span className='text-gray-400'>/</span>
              <span className='text-gray-600'>Edit</span>
            </>
          ) : (
            <>
              <span className='text-gray-400'>/</span>
              <span className='text-gray-600'>New Tender</span>
            </>
          )}
        </nav>
      </div>

      <div className='p-6 md:p-10 w-full mx-auto space-y-6'>
        <form onSubmit={(event) => event.preventDefault()} className='space-y-6'>
          <Controller
            name='tenderTitle'
            control={control}
            render={({ field }) => (
              <Input
                label='Tender Title'
                required={submitIntentRef.current === 'PUBLISH'}
                placeholder='Enter'
                value={field.value}
                onChange={field.onChange}
                error={errors.tenderTitle?.message}
                disabled={isBusy}
                $marginbottom='0'
              />
            )}
          />
  <div className='grid gap-4 md:grid-cols-2'>
          <Controller
            name='bidType'
            control={control}
            render={({ field }) => (
              <div>
                <Label className='mb-2 block'>Bid Type</Label>
                <CustomSelect
                  id='bidType'
                  name={field.name}
                  value={field.value}
                  onChange={(value) => field.onChange(Array.isArray(value) ? value[0] ?? '' : value)}
                  onBlur={() => field.onBlur()}
                  options={bidTypeOptions}
                  placeholder={isLoadingBidTypes ? 'Loading bid types...' : 'Select Bid Type'}
                  disabled={isBusy || isLoadingBidTypes}
                />
                {errors.bidType ? <Paragraph className='mt-2 text-sm text-red-600'>{errors.bidType.message}</Paragraph> : null}
              </div>
            )}
          />

          <Controller
            name='tenderCategory'
            control={control}
            render={({ field }) => (
              <div>
                <Label className='mb-2 block'>Tender Category</Label>
                <CustomSelect
                  id='tenderCategory'
                  name={field.name}
                  value={field.value}
                  onChange={(value) => field.onChange(Array.isArray(value) ? value[0] ?? '' : value)}
                  onBlur={() => field.onBlur()}
                  options={tenderCategoryOptions}
                  placeholder={isLoadingTenderCategories ? 'Loading tender categories...' : 'Select Category'}
                  disabled={isBusy || isLoadingTenderCategories}
                />
                {errors.tenderCategory ? <Paragraph className='mt-2 text-sm text-red-600'>{errors.tenderCategory.message}</Paragraph> : null}
              </div>
            )}
          />

          </div>

          <div className='grid gap-4 md:grid-cols-2'>
            <Controller
              name='startDate'
              control={control}
              render={({ field }) => (
                <Calendar
                  label='Start Date'
                  required={submitIntentRef.current === 'PUBLISH'}
                  selectedDate={field.value ? new Date(field.value) : null}
                  onChange={(date) => {
                    if (date instanceof Date) {
                      field.onChange(toDateOnly(date));
                    } else {
                      field.onChange('');
                    }
                  }}
                  error={errors.startDate?.message}
                  $isDisabled={isBusy}
                  $marginbottom='0'
                  icon={<Calendar1 />}
                  minDate={new Date()}
                />
              )}
            />

            <Controller
              name='endDate'
              control={control}
              render={({ field }) => (
                <Calendar
                  label='End Date'
                  required={submitIntentRef.current === 'PUBLISH'}
                  selectedDate={field.value ? new Date(field.value) : null}
                  onChange={(date) => {
                    if (date instanceof Date) {
                      field.onChange(toDateOnly(date));
                    } else {
                      field.onChange('');
                    }
                  }}
                  error={errors.endDate?.message}
                  $isDisabled={isBusy}
                  $marginbottom='0'
                  icon={<Calendar1 />}
                  minDate={new Date()}
                />
              )}
            />
          </div>

          <div className='rounded-xl border border-gray-200 bg-white p-4 md:p-5'>
            <Label className='mb-2 block'>Description</Label>
            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  value={field.value || ''}
                  onChange={field.onChange}
                  disabled={isBusy}
                  placeholder='Enter description'
                  className='border-gray-200'
                  toolbarContainerClassName='bg-gray-50 border-gray-200 px-4 py-2'
                  contentAreaClassName='min-h-[220px] max-h-[460px] p-3'
                />
              )}
            />
            {errors.description ? <Paragraph className='mt-2 text-sm text-red-600'>{errors.description.message}</Paragraph> : null}
          </div>

     

          <div className='rounded-xl border border-gray-200 bg-white p-4 md:p-5'>
            <Heading level={4} className='text-gray-900'>
              Tender Attachment
            </Heading>
            <Paragraph className='mt-1 text-sm text-gray-500'>Upload supporting tender file (max 50MB).</Paragraph>

            <Controller
              name='tenderUrl'
              control={control}
              render={({ field }) => (
                <>
                  <FileUpload
                    key={uploadKey}
                    ref={fileUploadRef}
                    onChange={handleFileSelection}
                    disabled={isBusy}
                    allowedFileTypes={['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
                    $maxSizeMB={50}
                    placeholder=''
                    containerClassName='mt-4 min-h-44 flex flex-col items-center justify-center gap-3 text-center bg-gray-50 border-gray-200'
                  >
                    <LucideFiles className='h-16 w-16 ' strokeWidth={1} />
                    <Paragraph className='font-semibold text-gray-700'>Drag file to upload</Paragraph>
                    <Paragraph className='text-xs text-gray-500'>Supported document files: PDF, DOC, DOCX. Max size: 50MB</Paragraph>
                    <Button type='button' variant='secondary'>
                      Browse File
                    </Button>
                  </FileUpload>

                  <input type='hidden' value={field.value || ''} onChange={field.onChange} />
                </>
              )}
            />

            {uploadedFile ? (
              <div className='mt-4 flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3'>
                <div className='flex items-center gap-3'>
                  <div className='flex h-16 w-16 items-center justify-center rounded-lg bg-red-50 text-sm font-semibold text-red-600'>
                    PDF
                  </div>
                  <div>
                    <Paragraph className='text-lg font-semibold text-gray-900'>{uploadedFile.name}</Paragraph>
                    <Paragraph className='text-sm text-gray-500'>PDF • {formatFileSize(uploadedFile.size)}</Paragraph>
                  </div>
                </div>

                <div className='flex items-center gap-2'>
                  <Button
                    type='button'
                    variant='secondary'
                    onClick={() => fileUploadRef.current?.click()}
                    disabled={isBusy}
                  >
                    Replace
                  </Button>
                  {isLocalUpload ? (
                    <button
                      type='button'
                      onClick={handleRemoveLocalUpload}
                      disabled={isBusy}
                      className='rounded-xl bg-red-50 p-3 hover:bg-red-100 disabled:opacity-50'
                      aria-label='Remove local uploaded file'
                    >
                      <TrashIcon fill='#DE534E' className='h-5 w-5' />
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {errors.tenderUrl ? <Paragraph className='mt-2 text-sm text-red-600'>{errors.tenderUrl.message}</Paragraph> : null}
          </div>

               <Controller
            name='submissionEmail'
            control={control}
            render={({ field }) => (
              <Input
                label='Submission Email'
                required
                type='email'
                placeholder='email@email.com'
                value={field.value}
                onChange={field.onChange}
                error={errors.submissionEmail?.message}
                disabled={isBusy}
                $marginbottom='0'
              />
            )}
          />
        </form>
      </div>
        </div>
    </div>
  );
};

export default TenderEditor;
